import React, { useEffect, useState, useCallback } from 'react';
import '../../styles/FrameValidation/VariantDetails.css';
import { QRCodeSVG } from 'qrcode.react';
import { useParams, useNavigate, useBeforeUnload } from 'react-router-dom';
import { getFirestore, doc, getDoc, updateDoc, collection, addDoc, getDocs, Timestamp } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Typography, Spin, Row, Col, Table, Button, Space, Input, Modal, App, ColorPicker, Descriptions } from 'antd';
import { CopyOutlined, EditOutlined, CheckOutlined, DeleteOutlined, PlusOutlined, SaveOutlined, InfoCircleOutlined, SyncOutlined, UserAddOutlined, ExpandOutlined, CompressOutlined } from '@ant-design/icons';
import { showInfoNotification, showSuccessNotification, showWarningNotification, showErrorNotification } from '../../utils/notifications';
import { IconButton } from '../../components/Components/Buttons/Buttons';
import ButtonState from '../../components/Components/Buttons/ButtonState';
import { buttonStyles, primaryShades, greyScaleTokens } from '../../theme/theme';
import { secureGetItem, decodePassword } from '../../data/utils';
import {
    determineNextStatus,
    isDeclineDisabled,
    isValidateDisabled,
} from '../services/Utils';
import { canView3DAssets } from '../services/Utils/RolePermissions';
import { LogVariantService } from '../services/Utils/LogVariantService';
import { NotificationService } from '../services/Utils/NotificationService';
import {
    handleGenerateUsdz,
    handleUsdzFallback,
    handleFileUpload,
    getButtonWithHoverStyle,
    isValidUrl,
    checkImageValidity
} from '../../services/utils/3DAssetsManager';
import DeclineModal from '../modal/DeclineModal';
import VariantAssignedToModal from '../modal/VariantAssignedToModal';
import TypeOfModellingModal from '../modal/TypeOfModellingModal';
import CustomTooltip from '../../components/Components/Modals/CustomTooltip';
import FullScreenModal from '../../components/Components/Modals/FullScreenModal';
import SmallModal from '../../components/Components/Modals/SmallModal';
import HeaderComponent from '../../components/Components/Headers/HeaderComponent';
import SimpleCard from '../../components/Components/Cards/SimpleCard';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ThreeDRotationIcon from '@mui/icons-material/ThreeDRotation';
import TryOnIcon from '../../assets/icon/Try-it-on-icon-centered.svg';

function VariantDetails() {
    const { variantId, brandId, productId } = useParams();
    const [variant, setVariant] = useState(null);
    const [isSingleAssignModalVisible, setIsSingleAssignModalVisible] = useState(false);
    const [loading, setLoading] = useState(true);
    const db = getFirestore();
    const navigate = useNavigate();
    const [editingKey, setEditingKey] = useState(null);
    const [originalUrl, setOriginalUrl] = useState(null);
    const role = secureGetItem("role");
    const [isDeclineModalVisible, setIsDeclineModalVisible] = useState(false);
    const [isModellingModalVisible, setIsModellingModalVisible] = useState(false);
    const [modellingLoading, setModellingLoading] = useState(false);
    const [pendingValidationAction, setPendingValidationAction] = useState(null);
    const [pendingUploads, setPendingUploads] = useState({});
    const [uploadingFiles, setUploadingFiles] = useState({});
    const [pendingHexChanges, setPendingHexChanges] = useState({});
    const [originalUrlValues, setOriginalUrlValues] = useState({});
    const [modelUrl, setModelUrl] = useState(null);
    const [iframeLoading, setIframeLoading] = useState(true);
    const [declineNote, setDeclineNote] = useState({
        date: Timestamp.now(),
        description: "",
        idVariant: variantId,
        nameProduct: "",
        images: [],
        declinedBy: secureGetItem("email") || "",
        skuModel: "",
        status: "NO READ",
        readBy: "",
        severity: undefined,
        causeDeclination: [],
        issueTitle: ""
    });
    const [validationNoteId, setValidationNoteId] = useState(null);
    const [editedFrameColor, setEditedFrameColor] = useState("");
    const [editedLensesColor, setEditedLensesColor] = useState("");
    const [originalHexValues, setOriginalHexValues] = useState({});
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [mainBrandWebDomain, setMainBrandWebDomain] = useState("safilo.webvto.it");
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [isGeneratingUsdz, setIsGeneratingUsdz] = useState(false);
    const encryptedEmail = localStorage.getItem("email");
    const userEmail = encryptedEmail ? decodePassword(encryptedEmail) : null;
    const isAssignedToUser = role === 'Modellista' && variant?.assignedTo === userEmail;
    const [isVtoModalVisible, setIsVtoModalVisible] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isRightCardExpanded, setIsRightCardExpanded] = useState(false);
    const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [validPosters, setValidPosters] = useState({
        poster: variant?.poster ? false : null,
        poster2: variant?.poster2 ? false : null,
        poster3: variant?.poster3 ? false : null,
        poster4: variant?.poster4 ? false : null
    });
    const [userProfiles, setUserProfiles] = useState({});
    // Colori che vengono usati per i componenti presi da ARSProductDetail.js
    const COLORS = {
        // Sfondi
        backgroundTransparent: 'transparent',
        backgroundSemiTransparent: 'rgba(255,255,255,0.7)',

        // Testo
        textPrimary: '#000000',

        // Bordi
        border: '#d9d9d9',

        // Stati
        selectedBackground: '#f0f5ff',

        // Componenti
        tryOnButton: greyScaleTokens.grey200,
        navigationIcon: primaryShades.primary300,
        navigationIconDisabled: primaryShades.primary100,
        modelIcon: primaryShades.primary300,
        modelIconBackground: primaryShades.primary100,
        modelIconDark: primaryShades.primary700
    };

    // Recupera tutti i profili utente una volta
    useEffect(() => {
        const fetchUserProfiles = async () => {
            try {
                const profilesRef = collection(db, "Profiles");
                const profilesSnapshot = await getDocs(profilesRef);
                const profilesData = {};

                profilesSnapshot.forEach((doc) => {
                    const data = doc.data();
                    // Handle both _email and email (if present)
                    const email = data._email || data.email;
                    if (email) {
                        profilesData[email] = {
                            firstName: data.firstName || '',
                            lastName: data.lastName || ''
                        };
                    }
                });

                setUserProfiles(profilesData);
            } catch (error) {
                console.error("Error fetching user profiles:", error);
            }
        };

        fetchUserProfiles();
    }, [db]);

    // Funzione per ottenere il nome completo dell'utente dall'email
    const getUserFullName = (email) => {
        if (!email) return '';

        // Controlla se l'email esiste esattamente come è
        const userProfile = userProfiles[email];
        if (userProfile && userProfile.firstName && userProfile.lastName) {
            return `${userProfile.firstName} ${userProfile.lastName}`;
        }

        // Prova una ricerca senza distinzione tra maiuscole e minuscole
        const lowerEmail = email.toLowerCase().trim();
        const matchingKey = Object.keys(userProfiles).find(key => key.toLowerCase().trim() === lowerEmail);
        if (matchingKey) {
            const profile = userProfiles[matchingKey];
            if (profile && profile.firstName && profile.lastName) {
                return `${profile.firstName} ${profile.lastName}`;
            }
        }

        return email; // Fallback all'email se il profilo non viene trovato
    };

    // Controlla se le immagini dei poster possono essere caricate correttamente
    useEffect(() => {
        // Funzione per controllare se un URL di immagine è valido utilizzando la funzione condivisa
        const checkImageUrl = async (url, posterKey) => {
            if (!url) return;

            const isValid = await checkImageValidity(url);

            setValidPosters(prev => ({
                ...prev,
                [posterKey]: isValid
            }));
        };

        // Controlla ogni URL del poster
        if (variant?.poster) checkImageUrl(variant.poster, 'poster');
        if (variant?.poster2) checkImageUrl(variant.poster2, 'poster2');
        if (variant?.poster3) checkImageUrl(variant.poster3, 'poster3');
        if (variant?.poster4) checkImageUrl(variant.poster4, 'poster4');
    }, [variant]);

    // Funzioni di navigazione per le immagini
    const goToNextImage = (maxIndex) => {
        setCurrentImageIndex((prev) =>
            prev < maxIndex ? prev + 1 : prev
        );
    };

    // Funzione per tornare all'immagine precedente
    const goToPrevImage = () => {
        setCurrentImageIndex((prev) =>
            prev > 0 ? prev - 1 : prev
        );
    };

    // Funzione per gestire il click sul pulsante di modifica
    const handleEditClick = () => {
        setIsEditModalVisible(true);
    };

    // Wrapper per la funzione di fallback
    const handleUsdzFallbackWrapper = async (relativePath, key) => {
        await handleUsdzFallback({
            relativePath,
            key,
            setPendingUploads
        });
    };

    // Funzione per generare il file USDZ da un file GLB
    const handleGenerateUsdzWrapper = async (key) => {
        await handleGenerateUsdz({
            variant,
            key,
            setPendingUploads,
            setIsGeneratingUsdz,
            setEditingKey
        });
    };

    // Funzione per chiudere la modale di modifica
    const handleEditModalClose = () => {
        setIsEditModalVisible(false);
    };

    // Funzione per verificare se un campo è modificabile in base al ruolo dell'utente
    const isFieldEditable = (field) => {
        if (role === 'Modellista') {
            return isAssignedToUser;
        }
        return true;
    };

    // Funzione per controllare se ci sono modifiche non salvate
    const checkUnsavedChanges = useCallback(() => {
        const frameColorChanged = editedFrameColor !== variant?.frameColor;
        const lensesColorChanged = editedLensesColor !== (variant?.lensesColor === 0 ? "TRANSPARENT" : variant?.lensesColor);

        const urlsChanged = Object.keys(originalUrlValues).some(key =>
            variant?.[key] !== originalUrlValues[key]
        );

        const hasPendingUploads = Object.keys(pendingUploads).length > 0;

        const hasPendingHexChanges = Object.keys(pendingHexChanges).length > 0;

        return frameColorChanged || lensesColorChanged || urlsChanged || hasPendingUploads || hasPendingHexChanges;
    }, [editedFrameColor, editedLensesColor, variant, originalUrlValues, pendingUploads, pendingHexChanges]);

    // Controlla se ci sono modifiche non salvate
    useEffect(() => {
        if (variant) {
            setHasUnsavedChanges(checkUnsavedChanges());
        }
    }, [variant, editedFrameColor, editedLensesColor, originalUrlValues, pendingUploads, pendingHexChanges, checkUnsavedChanges]);

    // Controlla se ci sono modifiche non salvate
    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (hasUnsavedChanges) {
                event.preventDefault();
                event.returnValue = "There are unsaved changes. Are you sure you want to leave the page?";
                return event.returnValue;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasUnsavedChanges]);

    // Controlla se ci sono modifiche non salvate
    useBeforeUnload(
        useCallback((event) => {
            if (hasUnsavedChanges) {
                event.preventDefault();
                return (event.returnValue = "There are unsaved changes. Are you sure you want to leave the page?");
            }
        }, [hasUnsavedChanges])
    );

    // Gestione della navigazione con controllo delle modifiche non salvate
    const handleNavigation = (destination) => {
        if (hasUnsavedChanges) {
            Modal.confirm({
                title: 'Unsaved changes',
                content: 'There are unsaved changes. Do you want to save before proceeding?',
                okText: 'Save and continue',
                cancelText: 'Discard changes',
                onOk: async () => {
                    await saveAllChanges();
                    if (typeof destination === 'function') {
                        destination();
                    } else {
                        navigate(destination);
                    }
                },
                onCancel: () => {
                    if (typeof destination === 'function') {
                        destination();
                    } else {
                        navigate(destination);
                    }
                }
            });
        } else {
            if (typeof destination === 'function') {
                destination();
            } else {
                navigate(destination);
            }
        }
    };

    // Funzione per salvare tutte le modifiche
    const saveAllChanges = async () => {
        try {
            const updates = {};
            let updateCount = 0;

            if (editedFrameColor !== variant.frameColor) {
                updates.frameColor = editedFrameColor;
                updateCount++;
            }

            const currentLensesColor = variant.lensesColor === 0 ? "TRANSPARENT" : variant.lensesColor;
            if (editedLensesColor !== currentLensesColor) {
                updates.lensesColor = editedLensesColor === "TRANSPARENT" ? 0 : editedLensesColor;
                updateCount++;
            }

            Object.keys(originalUrlValues).forEach(key => {
                if (variant[key] !== originalUrlValues[key]) {
                    updates[key] = variant[key];
                    updateCount++;
                    setOriginalUrlValues(prev => ({
                        ...prev,
                        [key]: variant[key]
                    }));
                }
            });

            let shouldReload = false;
            for (const [key, value] of Object.entries(pendingUploads)) {
                updates[key] = value;
                updateCount++;

                // Controlla se è stato salvato un caricamento per urlGlobal o urlGlobalComplete
                if (key === 'urlGlobal' || key === 'urlGlobalComplete') {
                    shouldReload = true;
                }
            }
            setPendingUploads({});

            for (const [key, value] of Object.entries(pendingHexChanges)) {
                const updateKey = hexKeyMapping[key];
                if (updateKey) {
                    updates[updateKey] = value;
                    updateCount++;
                }
            }
            setPendingHexChanges({});

            if (updateCount > 0) {
                updates.lastUpdate = Timestamp.now();

                const variantRef = doc(db, "Variants", variantId);
                await updateDoc(variantRef, updates);

                setVariant(prev => ({
                    ...prev,
                    ...updates
                }));

                showSuccessNotification("Save successful", `${updateCount} changes saved successfully!`);
                setHasUnsavedChanges(false);

                // Aggiorna solo il modello 3D se è stato salvato un caricamento per urlGlobal o urlGlobalComplete
                if (shouldReload) {
                    setTimeout(() => {
                        // Reset e ricarica del modello 3D
                        setModelUrl(null);
                        setTimeout(() => {
                            const baseUrl = process.env.REACT_APP_ACTIVE_BASE_URL || 'https://studio.arshades.it';
                            const modelViewerUrl = `${baseUrl}/Arshades3dLibrary/${productId}/variant/${variantId}`;
                            setModelUrl(modelViewerUrl);
                        }, 100);
                    }, 1000); // Piccolo delay per far vedere la notifica di successo
                }
            } else {
                showInfoNotification("No changes", "No changes to save");
            }
        } catch (error) {
            console.error("Error saving changes:", error);
            showErrorNotification("Error", "Error saving changes");
        }
    };

    // Recupera i dettagli della variante
    useEffect(() => {
        const fetchVariantDetails = async () => {
            try {
                const variantRef = doc(db, "Variants", variantId);
                const variantSnap = await getDoc(variantRef);
                if (!variantSnap.exists()) {
                    showErrorNotification("Error", "Variant not found");
                    setLoading(false);
                    return;
                }
                const variantData = variantSnap.data();

                if (!canView3DAssets(role)) {
                    // Mantieni urlGlobal per il bottone "Try on VTO" anche per i clienti
                    // ma rimuovi gli altri asset 3D
                    delete variantData.urlGlobalComplete;
                    delete variantData.urlUsdz;
                    delete variantData.urlUsdzComplete;
                }

                setOriginalUrlValues({
                    urlProductPage: variantData.urlProductPage || '',
                    urlDE: variantData.urlDE || '',
                    urlES: variantData.urlES || '',
                    urlFR: variantData.urlFR || ''
                });

                setVariant(variantData);
                setEditedFrameColor(variantData.frameColor || "");
                setEditedLensesColor(variantData.lensesColor === 0 ? "TRANSPARENT" : variantData.lensesColor || "");

                // Set the model URL for the 3D viewer
                // if (variantData.modelViewerUrl) {
                //     setModelUrl(variantData.modelViewerUrl);
                //     setIframeLoading(true);
                // } else {
                //     setModelUrl(null);
                // }
                // console.log("react", process.env.REACT_APP_ACTIVE_BASE_URL);
                // LINK TO 3D MODEL
                const baseUrl = process.env.REACT_APP_ACTIVE_BASE_URL || 'https://studio.arshades.it';
                const modelViewerUrl = `${baseUrl}/Arshades3dLibrary/${productId}/variant/${variantId}`;
                setModelUrl(modelViewerUrl);
            } catch (error) {
                console.error("Error fetching variant details:", error);
                showErrorNotification("Error", "Error fetching variant details");
            } finally {
                setLoading(false);
            }
        };

        fetchVariantDetails();
    }, [variantId, db, role]);

    // Recupera il webDomain del brand
    useEffect(() => {
        const fetchMainBrandWebDomain = async () => {
            try {
                const brandRef = doc(db, "Catalogues", brandId);
                const brandSnap = await getDoc(brandRef);

                if (brandSnap.exists()) {
                    const brandData = brandSnap.data();
                    if (brandData.webDomain) {
                        setMainBrandWebDomain(brandData.webDomain);
                        return;
                    }

                    const mainBrandsRef = collection(db, "MainBrands");
                    const mainBrandsSnap = await getDocs(mainBrandsRef);

                    for (const mainBrandDoc of mainBrandsSnap.docs) {
                        const mainBrandData = mainBrandDoc.data();
                        if (mainBrandData.catalogsList && mainBrandData.catalogsList.includes(brandId)) {
                            if (mainBrandData.webDomain) {
                                setMainBrandWebDomain(mainBrandData.webDomain);
                                return;
                            }
                        }
                    }
                }
            } catch (error) {
                console.error("Errore nel recupero del webDomain:", error);
            }
        };

        fetchMainBrandWebDomain();
    }, [brandId, db]);

    // Mostra lo spinner di caricamento se la pagina è ancora in fase di caricamento
    if (loading) return <Spin />;

    // Mostra un messaggio di errore se la variante non è disponibile
    if (!variant) return <Typography.Text>Variante non disponibile</Typography.Text>;

    // Mappa gli stati della variante
    const statusMapping = {
        "Rev. Modellista": { text: "Modelist Rev.", color: "#FF6347" },
        "Rev. Cliente": { text: "Client Rev.", color: "#FFA500" },
        "Rev. Spaarkly": { text: "Spaarkly Rev.", color: "#FFD700" },
        "Incompleto": { text: "Incomplete", color: "#808080" },
        "In Pubblicazione": { text: "In Publication", color: "#008000" },
        "Pubblicato": { text: "Published", color: "#008000" }
    };

    // Mostra lo stato della variante
    const status = statusMapping[variant.status] || { text: variant.status, color: "#000" };

    // Mostra i colori esadecimali
    const hexColors = [
        {
            key: 'Primary Frame Color',
            value: variant.primaryFrameColorHex,
            color: variant.primaryFrameColorHex
        },
        {
            key: 'Primary Lenses Color',
            value: variant.primaryLensesColorHex,
            color: variant.primaryLensesColorHex
        },
        {
            key: 'Secondary Frame Color',
            value: variant.secondaryFrameColorHex,
            color: variant.secondaryFrameColorHex
        },
        {
            key: 'Secondary Lenses Color',
            value: variant.secondaryLensesColorHex,
            color: variant.secondaryLensesColorHex
        }
    ];

    // Mappa le chiavi dei colori esadecimali
    const hexKeyMapping = {
        'Primary Frame Color': 'primaryFrameColorHex',
        'Primary Lenses Color': 'primaryLensesColorHex',
        'Secondary Frame Color': 'secondaryFrameColorHex',
        'Secondary Lenses Color': 'secondaryLensesColorHex'
    };

    // Funzione per aggiornare un singolo URL
    const updateSingleUrl = async (key, value) => {
        try {
            const variantRef = doc(db, "Variants", variantId);
            await updateDoc(variantRef, { [key]: value });

            setVariant(prevVariant => ({
                ...prevVariant,
                [key]: value
            }));

            if (['urlProductPage', 'urlDE', 'urlES', 'urlFR'].includes(key)) {
                setOriginalUrlValues(prev => ({
                    ...prev,
                    [key]: value
                }));
            }

            setPendingUploads(prev => {
                const updated = { ...prev };
                delete updated[key];
                return updated;
            });
        } catch (error) {
            console.error("Error updating URL:", error);
            showErrorNotification("Error", "Error updating URL");
        }
    };

    // Funzione per annullare un caricamento in sospeso
    const cancelPendingUpload = (key) => {
        setPendingUploads(prev => {
            const updated = { ...prev };
            delete updated[key];
            return updated;
        });
        showInfoNotification("Modification cancelled", "The changes have been cancelled successfully.");
    };

    // Funzione per confermare un caricamento in sospeso
    const confirmPendingUpload = async (key) => {
        const newUrl = pendingUploads[key];
        if (newUrl) {
            await updateSingleUrl(key, newUrl);

            // Aggiorna solo il modello 3D se è stato confermato un caricamento per urlGlobal o urlGlobalComplete
            if (key === 'urlGlobal' || key === 'urlGlobalComplete') {
                setTimeout(() => {
                    // Reset e ricarica del modello 3D
                    setModelUrl(null);
                    setTimeout(() => {
                        const baseUrl = process.env.REACT_APP_ACTIVE_BASE_URL || 'https://studio.arshades.it';
                        const modelViewerUrl = `${baseUrl}/Arshades3dLibrary/${productId}/variant/${variantId}`;
                        setModelUrl(modelViewerUrl);
                    }, 100);
                }, 1000); // Piccolo delay per permettere il salvataggio
            }
        }
    };

    // Funzione per salvare un URL
    const saveUrl = async (key, newUrl) => {
        if (newUrl) {
            await updateSingleUrl(key, newUrl);
            setEditingKey(null);
        }
    };

    // Funzione per mostrare la modale di conferma
    const showConfirmModal = (action) => {
        setPendingAction(action);
        setIsConfirmModalVisible(true);
    };

    // Funzione per eseguire l'azione confermata
    const executeConfirmedAction = async () => {
        if (!pendingAction) return;

        setIsConfirmModalVisible(false);
        const action = pendingAction;
        setPendingAction(null);

        // Esegui l'azione confermata
        await executeActionAfterConfirm(action);
    };

    // Funzione per eseguire effettivamente l'aggiornamento dello stato
    const performVariantStatusUpdate = async (action) => {
        const newStatus = determineNextStatus(role, variant.status, action);

        if (action === "validate" && variant.status === "Rev. Modellista" && (role === "Modellista" || role === "ModellerSupervisor")) {
            setPendingValidationAction({ action, newStatus });
            setIsModellingModalVisible(true);
            return;
        }

        if (newStatus !== variant.status) {
            try {
                const variantRef = doc(db, "Variants", variantId);
                const updateData = {
                    status: newStatus,
                    lastUpdate: Timestamp.now()
                };

                // Se stiamo pubblicando (da "In Pubblicazione" a "Pubblicato")
                if (variant.status === "In Pubblicazione" && newStatus === "Pubblicato" && action === "validate") {
                    updateData.publishedInARSLibrary = Timestamp.now();

                    // Aggiorna lo stato del prodotto padre e incrementa availableVariants
                    if (variant.productRef) {
                        try {
                            const productRef = doc(db, "Products", variant.productRef);
                            const productSnap = await getDoc(productRef);

                            if (productSnap.exists()) {
                                const productData = productSnap.data();
                                const currentAvailableVariants = productData.availableVariants || 0;

                                // Prepara la mappa della variante per catalogues_variants_map
                                const variantMap = {
                                    eanCode: variant.eanCode || "",
                                    id: variantId,
                                    skuCode: variant.skuModel || "",
                                    upcCode: variant.upcCode || "",
                                    variantName: variant.glassesName || "",
                                    publishedInARSLibrary: Timestamp.now()
                                };

                                // Ottieni l'array esistente o creane uno nuovo
                                const currentVariantsMap = productData.variants_map || [];

                                // Controlla se la variante esiste già nell'array
                                const existingVariantIndex = currentVariantsMap.findIndex(v => v.id === variantId);

                                let updatedVariantsMap;
                                if (existingVariantIndex !== -1) {
                                    // Se esiste, aggiorna la mappa esistente
                                    updatedVariantsMap = [...currentVariantsMap];
                                    updatedVariantsMap[existingVariantIndex] = variantMap;
                                } else {
                                    // Se non esiste, aggiungi la nuova mappa
                                    updatedVariantsMap = [...currentVariantsMap, variantMap];
                                }

                                await updateDoc(productRef, {
                                    status: "Pubblicato",
                                    availableVariants: currentAvailableVariants + 1,
                                    lastPublishedInARSLibrary: Timestamp.now(),
                                    variants_map: updatedVariantsMap
                                });
                                console.log(`Product ${variant.productRef} status updated to "Pubblicato" and availableVariants incremented to ${currentAvailableVariants + 1}`);

                            } else {
                                console.error(`Product ${variant.productRef} not found`);
                            }
                        } catch (productError) {
                            console.error("Error updating product status and availableVariants:", productError);
                            // Non blocchiamo l'operazione principale se l'aggiornamento del prodotto fallisce
                        }
                    }
                }

                await updateDoc(variantRef, updateData);

                // Aggiorna anche il lastUpdate del prodotto padre
                if (variant.productRef) {
                    try {
                        const productRef = doc(db, "Products", variant.productRef);
                        await updateDoc(productRef, {
                            lastUpdate: Timestamp.now()
                        });
                    } catch (productError) {
                        console.error("Error updating product lastUpdate:", productError);
                        // Non blocchiamo l'operazione principale se l'aggiornamento del prodotto fallisce
                    }
                }

                if (action === "validate") {
                    // Se stiamo pubblicando, passa i dati speciali al log
                    if (variant.status === "In Pubblicazione" && newStatus === "Pubblicato") {
                        const publishData = {
                            account: secureGetItem("email"),
                            role: secureGetItem("role"),
                            time: Timestamp.now(),
                            modeling_note: "Published in ARS Library"
                        };
                        await LogVariantService.logValidateVariant(variantId, newStatus, publishData);
                    } else {
                        await LogVariantService.logValidateVariant(variantId, newStatus);
                    }

                    // Crea notifiche per la validazione
                    await NotificationService.createVariantValidationNotifications(
                        variantId,
                        brandId,
                        variant.skuModel || "",
                        newStatus,
                        true, // Notifica anche all'utente corrente
                        variant.modelName || variant.glassesName || "",
                        productId, // Aggiungiamo l'ID del prodotto
                        variant.assignedTo // Aggiungiamo l'utente assegnato
                    );
                } else if (action === "start") {
                    await LogVariantService.logInitVariant(variantId);
                }

                showSuccessNotification("Save successful", "Status updated successfully!");
                setVariant(prevVariant => ({
                    ...prevVariant,
                    status: newStatus,
                    ...(updateData.publishedInARSLibrary && { publishedInARSLibrary: updateData.publishedInARSLibrary }),
                    ...(updateData.availableVariants && { availableVariants: updateData.availableVariants })
                }));
            } catch (error) {
                console.error("Error updating status:", error);
                showErrorNotification("Error", "Error updating status");
            }
        }
    };

    // Funzione per aggiornare lo stato della variante (con modale di conferma)
    const updateVariantStatus = async (action) => {
        // Mostra sempre la modale di conferma per tutte le azioni
        showConfirmModal(action);
    };

    // Funzione per eseguire l'azione dopo la conferma
    const executeActionAfterConfirm = async (action) => {
        if (action === "decline" && !isDeclineDisabled(role, variant.status)) {
            setDeclineNote(prev => ({
                ...prev,
                date: Timestamp.now(),
                idVariant: variantId,
                nameProduct: variant.glassesName || "",
                images: [],
                skuModel: variant.skuModel
            }));
            setIsDeclineModalVisible(true);
            return;
        }

        // Per validate, start e altre azioni, esegui l'aggiornamento dello stato
        await performVariantStatusUpdate(action);
    };

    // Funzione per gestire l'invio dei dati di modellazione
    const handleModellingSubmit = async (modellingData) => {
        if (!pendingValidationAction) return;

        setModellingLoading(true);

        try {
            const { action, newStatus } = pendingValidationAction;
            const variantRef = doc(db, "Variants", variantId);

            // Aggiorna lo stato della variante
            await updateDoc(variantRef, {
                status: newStatus,
                lastUpdate: Timestamp.now()
            });

            // Aggiorna anche il lastUpdate del prodotto padre
            if (variant.productRef) {
                try {
                    const productRef = doc(db, "Products", variant.productRef);
                    await updateDoc(productRef, {
                        lastUpdate: Timestamp.now()
                    });
                } catch (productError) {
                    console.error("Error updating product lastUpdate:", productError);
                    // Non blocchiamo l'operazione principale se l'aggiornamento del prodotto fallisce
                }
            }

            // Registra la validazione con i dati di modellazione
            await LogVariantService.logValidateVariant(variantId, newStatus, modellingData);

            // Crea notifiche per la validazione
            await NotificationService.createVariantValidationNotifications(
                variantId,
                brandId,
                variant.skuModel || "",
                newStatus,
                true, // Notifica anche all'utente corrente
                variant.modelName || variant.glassesName || "",
                productId, // Aggiungiamo l'ID del prodotto
                variant.assignedTo // Aggiungiamo l'utente assegnato
            );

            // Recupera e visualizza il documento LogVariant
            const variantData = await getDoc(variantRef);
            if (variantData.exists()) {
                const data = variantData.data();
                if (data.refLogVariant) {
                    const logRef = doc(db, "LogVariant", data.refLogVariant);
                    await getDoc(logRef);
                }
            }

            showSuccessNotification("Save successful", "Status updated successfully!");
            setVariant(prevVariant => ({
                ...prevVariant,
                status: newStatus
            }));

            // Chiudi la modale e resetta lo stato
            setIsModellingModalVisible(false);
            setPendingValidationAction(null);
        } catch (error) {
            console.error("Error updating status:", error);
            showErrorNotification("Error", "Error updating status");
        } finally {
            setModellingLoading(false);
        }
    };

    // Funzione per gestire il caricamento delle immagini
    const handleImageUpload = async ({ file, fileList }) => {
        try {
            if (file.status === 'removed') {
                const updatedImages = declineNote.images.filter((_, index) =>
                    index !== fileList.findIndex(item => item.uid === file.uid)
                );
                setDeclineNote(prev => ({ ...prev, images: updatedImages }));
                return;
            }

            if (file.status !== 'uploading') return;

            file.status = 'done';
        } catch (error) {
            file.status = 'error';
            showErrorNotification("Error", `Error uploading image ${file.name}`);
            console.error("Error uploading image:", error);
        }
    };

    // Funzione per rimuovere un'immagine
    const handleRemoveImage = (index) => {
        const updatedImages = [...declineNote.images];
        updatedImages.splice(index, 1);
        setDeclineNote(prev => ({ ...prev, images: updatedImages }));
    };

    // Funzione per gestire l'invio del modulo di rifiuto
    const handleDeclineSubmit = async (submittedNote) => {
        try {
            // Use the submitted note which contains the validated causeDeclination field
            const noteToSave = submittedNote || declineNote;

            if (!noteToSave.issueTitle?.trim()) {
                showWarningNotification("Warning", "Please enter an issue title");
                return;
            }

            if (!noteToSave.description.trim()) {
                showWarningNotification("Warning", "Please enter a description for the decline");
                return;
            }

            if (!noteToSave.causeDeclination ||
                (Array.isArray(noteToSave.causeDeclination) && noteToSave.causeDeclination.length === 0)) {
                showWarningNotification("Warning", "Please select at least one reason for declination");
                return;
            }

            if (!noteToSave.severity) {
                showWarningNotification("Warning", "Please select a severity level");
                return;
            }

            // Verifica che sia stata caricata almeno un'immagine di riferimento quando è selezionato "Incorrect Reference Image"
            if (noteToSave.causeDeclination?.includes("Incorrect Reference Image") &&
                (!noteToSave.referenceImageCorrect || noteToSave.referenceImageCorrect.length === 0)) {
                showWarningNotification("Warning", "Please upload at least one correct reference image");
                return;
            }

            const noteDataToSave = {
                ...noteToSave,
                images: noteToSave.images || [],
                date: Timestamp.now(),
                status: "NO READ",
                readBy: ""
            };

            const validationNotesRef = collection(db, "ValidationNotes");
            const newNoteRef = await addDoc(validationNotesRef, noteDataToSave);

            setValidationNoteId(newNoteRef.id);

            const variantRef = doc(db, "Variants", variantId);

            const updatedListNotes = variant.list_notes
                ? [...variant.list_notes, newNoteRef.id]
                : [newNoteRef.id];

            const newStatus = determineNextStatus(role, variant.status, "decline");
            await updateDoc(variantRef, {
                status: newStatus,
                list_notes: updatedListNotes,
                lastUpdate: Timestamp.now()
            });

            // Aggiorna anche il lastUpdate del prodotto padre
            if (variant.productRef) {
                try {
                    const productRef = doc(db, "Products", variant.productRef);
                    await updateDoc(productRef, {
                        lastUpdate: Timestamp.now()
                    });
                } catch (productError) {
                    console.error("Error updating product lastUpdate:", productError);
                    // Non blocchiamo l'operazione principale se l'aggiornamento del prodotto fallisce
                }
            }

            await LogVariantService.logDeclineVariant(variantId, noteDataToSave);

            // Crea notifiche per il decline della variante
            await NotificationService.createVariantDeclineNotifications(
                variantId,
                brandId,
                variant.skuModel || "",
                newStatus,
                newNoteRef.id,
                true, // Notifica anche all'utente corrente
                variant.modelName || variant.glassesName || "",
                productId, // Aggiungiamo l'ID del prodotto
                variant.assignedTo // Aggiungiamo l'utente assegnato
            );

            setVariant(prevVariant => ({
                ...prevVariant,
                status: newStatus,
                list_notes: updatedListNotes
            }));

            showSuccessNotification("Save successful", "Variant declined successfully!");
            setIsDeclineModalVisible(false);

            setDeclineNote({
                date: Timestamp.now(),
                description: "",
                idVariant: variantId,
                nameProduct: variant.glassesName || "",
                images: [],
                skuModel: variant.skuModel
            });
        } catch (error) {
            console.error("Error saving decline note:", error);
            showErrorNotification("Error", "Error saving decline note");
        }
    };

    // Funzione per confermare una modifica del colore esadecimale in sospeso
    const confirmPendingHexChange = async (key) => {
        const newHexValue = pendingHexChanges[key];
        if (newHexValue) {
            try {
                const variantRef = doc(db, "Variants", variantId);
                const updateKey = hexKeyMapping[key];
                if (!updateKey) {
                    console.error("Key not found in mapping:", key);
                    return;
                }
                await updateDoc(variantRef, { [updateKey]: newHexValue });
                showSuccessNotification("Save successful", "Color updated successfully!");

                setVariant(prevVariant => ({
                    ...prevVariant,
                    [updateKey]: newHexValue
                }));

                setPendingHexChanges(prev => {
                    const updated = { ...prev };
                    delete updated[key];
                    return updated;
                });
            } catch (error) {
                console.error("Error updating color:", error);
                showErrorNotification("Error", "Error updating color");
            }
        }
    };

    // Funzione per annullare una modifica del colore esadecimale in sospeso
    const cancelPendingHexChange = (key) => {
        setPendingHexChanges(prev => {
            const updated = { ...prev };
            delete updated[key];
            return updated;
        });
        setVariant(prevVariant => ({
            ...prevVariant,
            [hexKeyMapping[key]]: originalHexValues[key] || prevVariant[hexKeyMapping[key]]
        }));
        showInfoNotification("Modification cancelled", "The changes have been cancelled successfully.");
    };

    // Stile comune per tutti i bottoni nella colonna Actions
    const actionButtonStyle = {
        width: '32px',
        height: '32px',
        minWidth: '32px',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #d9d9d9',
        borderRadius: '6px',
        transition: 'all 0.3s'
    };

    // Stile per il contenitore dei bottoni con hover effect
    const getButtonWithHoverStyleWrapper = (disabled) => {
        return getButtonWithHoverStyle(disabled, actionButtonStyle, primaryShades);
    };

    // Stile per il contenitore dei bottoni
    const actionContainerStyle = {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '8px'
    };

    // Mostra le colonne con le icone
    const columnsWithIcons = [
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <span>Key</span>
                    <CustomTooltip title="Key of the reference image">
                        <InfoCircleOutlined style={{ marginLeft: 'clamp(0.4rem, 0.35rem + 0.25vw, 0.5rem)', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'key',
            key: 'key',
            align: 'left',
            width: '25%',
            render: (text, record) => (
                <CustomTooltip title={`Attribute: ${record.displayName || text}`}>
                    <span>{record.displayName || text}</span>
                </CustomTooltip>
            )
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <span>URL</span>
                    <CustomTooltip title="URL address of the reference image">
                        <InfoCircleOutlined style={{ marginLeft: 'clamp(0.4rem, 0.35rem + 0.25vw, 0.5rem)', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'url',
            key: 'url',
            render: (text, record) => {
                const pendingUrl = pendingUploads[record.key];

                if (editingKey === record.key) {
                    return (
                        <Input
                            defaultValue={text}
                            onChange={(e) => setVariant(prev => ({
                                ...prev,
                                [record.key]: e.target.value
                            }))}
                            style={{ width: '100%' }}
                            autoFocus
                        />
                    );
                } else {
                    const displayUrl = pendingUrl || text;

                    // Utilizziamo la funzione condivisa isValidUrl

                    if (!displayUrl) {
                        return <Typography.Text type="secondary">Empty</Typography.Text>;
                    }

                    // Se è un URL valido, mostralo come link cliccabile
                    if (isValidUrl(displayUrl)) {
                        return (
                            <Typography.Link
                                href={displayUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    maxWidth: '150px',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    display: 'inline-block',
                                    color: pendingUrl ? '#1890ff' : '#000'
                                }}
                            >
                                {displayUrl}
                            </Typography.Link>
                        );
                    }

                    // Altrimenti, mostra il valore esatto come testo normale
                    return (
                        <Typography.Text
                            style={{
                                maxWidth: '150px',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                display: 'inline-block',
                                color: pendingUrl ? '#1890ff' : '#000'
                            }}
                        >
                            {displayUrl}
                        </Typography.Text>
                    );
                }
            },
            align: 'left',
            ellipsis: true,
            width: '35%'
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <span>Actions</span>
                    <CustomTooltip title="Actions available for the item">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            key: 'actions',
            align: 'right',
            width: '40%',
            render: (text, record) => {
                const pendingUrl = pendingUploads[record.key];
                const isUploading = uploadingFiles[record.key] === true;
                const isGenerating = isGeneratingUsdz && record.key === editingKey;

                if (isUploading || isGenerating) {
                    return (
                        <div style={actionContainerStyle}>
                            <Spin size="small" tip={isGenerating ? "Generating..." : "Uploading..."} />
                        </div>
                    );
                }

                if (pendingUrl) {
                    return (
                        <div style={actionContainerStyle}>
                            <IconButton
                                icon={<CheckOutlined />}
                                tooltip="Confirm"
                                style={{ ...buttonStyles.success, ...getButtonWithHoverStyleWrapper(!isFieldEditable('referenceImages')) }}
                                onClick={() => confirmPendingUpload(record.key)}
                                disabled={!isFieldEditable('referenceImages')}
                            />
                            <IconButton
                                icon={<DeleteOutlined />}
                                tooltip="Cancel"
                                style={{ ...buttonStyles.error, ...getButtonWithHoverStyleWrapper(!isFieldEditable('referenceImages')) }}
                                onClick={() => cancelPendingUpload(record.key)}
                                disabled={!isFieldEditable('referenceImages')}
                            />
                        </div>
                    );
                }

                if (editingKey === record.key) {
                    return (
                        <div style={actionContainerStyle}>
                            <IconButton
                                icon={<CheckOutlined />}
                                tooltip="Confirm"
                                style={{ ...buttonStyles.success, ...getButtonWithHoverStyleWrapper(!isFieldEditable('referenceImages')) }}
                                onClick={() => saveUrl(record.key, variant[record.key])}
                                disabled={!isFieldEditable('referenceImages')}
                            />
                            <IconButton
                                icon={<DeleteOutlined />}
                                tooltip="Cancel"
                                style={{ ...buttonStyles.error, ...getButtonWithHoverStyleWrapper(!isFieldEditable('referenceImages')) }}
                                onClick={() => {
                                    setVariant(prev => ({
                                        ...prev,
                                        [record.key]: originalUrl
                                    }));
                                    setEditingKey(null);
                                    showInfoNotification("Modification cancelled", "The changes have been cancelled successfully.");
                                }}
                                disabled={!isFieldEditable('referenceImages')}
                            />
                        </div>
                    );
                }

                return (
                    <div style={actionContainerStyle}>
                        <IconButton
                            icon={<CopyOutlined />}
                            tooltip="Copy URL"
                            onClick={() => {
                                navigator.clipboard.writeText(record.url);
                                showSuccessNotification("Copied", "Link copied!");
                            }}
                            disabled={!isFieldEditable('referenceImages')}
                            style={getButtonWithHoverStyleWrapper(!isFieldEditable('referenceImages'))}
                        />
                        {['urlGlobal', 'urlGlobalComplete'].includes(record.key) ? (
                            <Button
                                type="text"
                                icon={<PlusOutlined />}
                                style={getButtonWithHoverStyleWrapper(isUploading || !isFieldEditable('3dAssets'))}
                                disabled={isUploading || !isFieldEditable('3dAssets')}
                                onClick={() => {
                                    // Simulare il click sul file input nascosto
                                    const fileInput = document.getElementById(`upload-${record.key}`);
                                    if (fileInput) fileInput.click();
                                }}
                            />
                        ) : ['poster', 'poster2', 'poster3', 'poster4', 'posterNoBackground'].includes(record.key) ? (
                            <Button
                                type="text"
                                icon={<PlusOutlined />}
                                style={getButtonWithHoverStyleWrapper(isUploading || !isFieldEditable('referenceImages'))}
                                disabled={isUploading || !isFieldEditable('referenceImages')}
                                onClick={() => {
                                    // Simulare il click sul file input nascosto
                                    const fileInput = document.getElementById(`upload-${record.key}`);
                                    if (fileInput) fileInput.click();
                                }}
                            />
                        ) : ['urlUsdz', 'urlUsdzComplete'].includes(record.key) ? (
                            <Button
                                type="text"
                                icon={<SyncOutlined />}
                                style={getButtonWithHoverStyleWrapper(
                                    !isFieldEditable('3dAssets') ||
                                    isGeneratingUsdz ||
                                    !variant[record.key === 'urlUsdz' ? 'urlGlobal' : 'urlGlobalComplete']
                                )}
                                onClick={() => handleGenerateUsdzWrapper(record.key)}
                                disabled={
                                    !isFieldEditable('3dAssets') ||
                                    isGeneratingUsdz ||
                                    !variant[record.key === 'urlUsdz' ? 'urlGlobal' : 'urlGlobalComplete']
                                }
                            />
                        ) : (
                            <IconButton
                                icon={<EditOutlined />}
                                tooltip="Edit URL"
                                style={getButtonWithHoverStyleWrapper(!isFieldEditable('referenceImages'))}
                                onClick={() => {
                                    setOriginalUrl(record.url);
                                    setEditingKey(record.key);
                                }}
                                disabled={!isFieldEditable('referenceImages')}
                            />
                        )}

                        {/* Hidden file inputs for uploads */}
                        {['urlGlobal', 'urlGlobalComplete'].includes(record.key) && (
                            <input
                                id={`upload-${record.key}`}
                                type="file"
                                accept=".glb"
                                style={{ display: 'none' }}
                                onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;

                                    await handleFileUpload({
                                        file,
                                        skuModel: variant.skuModel,
                                        recordKey: record.key,
                                        setPendingUploads,
                                        setUploadingFiles
                                    });

                                    // Reset the input
                                    e.target.value = null;
                                }}
                            />
                        )}

                        {['poster', 'poster2', 'poster3', 'poster4', 'posterNoBackground'].includes(record.key) && (
                            <input
                                id={`upload-${record.key}`}
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;

                                    await handleFileUpload({
                                        file,
                                        skuModel: variant.skuModel,
                                        recordKey: record.key,
                                        setPendingUploads,
                                        setUploadingFiles
                                    });

                                    // Reset the input
                                    e.target.value = null;
                                }}
                            />
                        )}
                    </div>
                );
            }
        }
    ];

    return (
        <App>
            <div
                style={{
                    width: "100%",
                    borderRadius: "1rem",
                }}
            >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <HeaderComponent
                            buttonIcon={<NavigateBeforeIcon style={{ fontSize: '1.5rem' }} />}
                            buttonText=""
                            onButtonClick={() => handleNavigation(-1)}
                            isVisible={true}
                            buttonPosition="left"
                            noCamp={true}
                            title={`Variant ${variant?.glassesName || 'N/A'} ${variant?.frameColor || 'N/A'} Details`}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                        {(role === 'Admin' || role === 'ModellerSupervisor') && (
                            <CustomTooltip title={variant?.assignedTo ? `Currently assigned to ${getUserFullName(variant.assignedTo)}` : "Assign this variant to a user"}>
                                <Button
                                    type="primary"
                                    size="middle"
                                    icon={<UserAddOutlined style={{ fontSize: '14px' }} />}
                                    style={{
                                        borderRadius: '6px',
                                        fontWeight: '500',
                                        background: 'linear-gradient(135deg, #8e44ad 0%, #9b59b6 100%)',
                                        borderColor: '#8e44ad',
                                        color: 'white',
                                        boxShadow: '0 2px 0 rgba(0, 0, 0, 0.045)',
                                        height: '32px',
                                        padding: '0 12px',
                                        fontSize: '14px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onClick={() => setIsSingleAssignModalVisible(true)}
                                >
                                    {variant.assignedTo ? "Change Assignee" : "Assign To"}
                                </Button>
                            </CustomTooltip>
                        )}
                        <CustomTooltip title="Save all changes">
                            <Button
                                type="primary"
                                icon={<SaveOutlined style={{ fontSize: '14px' }} />}
                                size="middle"
                                onClick={saveAllChanges}
                                disabled={!hasUnsavedChanges}
                                style={{
                                    borderRadius: '6px',
                                    fontWeight: '500',
                                    background: hasUnsavedChanges ? 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)' : '#f5f5f5',
                                    borderColor: hasUnsavedChanges ? '#1890ff' : '#d9d9d9',
                                    color: hasUnsavedChanges ? 'white' : 'rgba(0, 0, 0, 0.25)',
                                    boxShadow: hasUnsavedChanges ? '0 2px 0 rgba(0, 0, 0, 0.045)' : 'none',
                                    height: '32px',
                                    padding: '0 12px',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                SAVE
                            </Button>
                        </CustomTooltip>
                    </div>
                </div>

                {/* Main card */}
                <Row gutter={[24, 24]} style={{ marginTop: 40 }}>
                    {/* SimpleCard posizionata a sinistra  */}
                    <Col xs={24} sm={24} md={isRightCardExpanded ? 24 : 8}
                        style={{
                            transition: 'all 0.3s ease',
                            order: isRightCardExpanded ? 1 : 0,
                            marginTop: isRightCardExpanded ? '24px' : '0px'
                        }}>
                        <SimpleCard
                            title={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontWeight: '600' }}>Variant Information</span>
                                    <ButtonState
                                        state={variant.status || 'Incomplete'}
                                        text={(statusMapping[variant.status]?.text) || variant.status || 'N/A'}
                                    />
                                </div>
                            }
                            style={{ height: 'auto' }}
                            padding="0"
                        >
                            <div style={{ padding: '16px' }}>
                                {/* Variant Information */}
                                <Descriptions
                                    bordered
                                    size="small"
                                    column={1}
                                >
                                    <Descriptions.Item label="Name">
                                        <Typography.Link
                                            href={`/framevalidation/${brandId}/${productId}`}
                                            style={{
                                                textDecoration: 'underline',
                                                color: '#000000',
                                                fontWeight: '500'
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleNavigation(`/framevalidation/${brandId}/${productId}`);
                                            }}
                                        >
                                            {variant.glassesName || 'N/A'}
                                        </Typography.Link>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="SKU">{variant.skuModel || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="Size">{variant.size?.size || 'N/A'}</Descriptions.Item>

                                    <Descriptions.Item label="Frame Color">
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <Input
                                                value={editedFrameColor}
                                                onChange={(e) => setEditedFrameColor(e.target.value)}
                                                placeholder="Frame Color"
                                                disabled={!isFieldEditable('frameColor')}
                                                style={{ flex: 1 }}
                                            />
                                            {editedFrameColor !== variant.frameColor && (
                                                <Space style={{ marginLeft: '8px' }}>
                                                    <Button
                                                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                                                        type="primary"
                                                        shape="circle"
                                                        size="small"
                                                        icon={<CheckOutlined />}
                                                        onClick={() => updateSingleUrl("frameColor", editedFrameColor)}
                                                    />
                                                    <Button
                                                        style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: 'white' }}
                                                        shape="circle"
                                                        size="small"
                                                        icon={<DeleteOutlined />}
                                                        onClick={() => setEditedFrameColor(variant.frameColor)}
                                                    />
                                                </Space>
                                            )}
                                        </div>
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Lenses Color">
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <Input
                                                value={editedLensesColor}
                                                onChange={(e) => setEditedLensesColor(e.target.value)}
                                                placeholder="Lenses Color"
                                                disabled={!isFieldEditable('lensesColor')}
                                                style={{ flex: 1 }}
                                            />
                                            {editedLensesColor !== (variant.lensesColor === 0 ? "TRANSPARENT" : variant.lensesColor) && (
                                                <Space style={{ marginLeft: '8px' }}>
                                                    <Button
                                                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                                                        type="primary"
                                                        shape="circle"
                                                        size="small"
                                                        icon={<CheckOutlined />}
                                                        onClick={() => updateSingleUrl("lensesColor", editedLensesColor === "TRANSPARENT" ? 0 : editedLensesColor)}
                                                    />
                                                    <Button
                                                        style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: 'white' }}
                                                        shape="circle"
                                                        size="small"
                                                        icon={<DeleteOutlined />}
                                                        onClick={() => setEditedLensesColor(variant.lensesColor === 0 ? "TRANSPARENT" : variant.lensesColor)}
                                                    />
                                                </Space>
                                            )}
                                        </div>
                                    </Descriptions.Item>

                                    <Descriptions.Item label="EAN">{variant.eanCode || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="UPC">{variant.upcCode || 'N/A'}</Descriptions.Item>

                                    <Descriptions.Item label="Temple Color">{variant.templeColor || 'N/A'}</Descriptions.Item>

                                    <Descriptions.Item label="VTO QR Code">
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <QRCodeSVG
                                                value={`https://www.${mainBrandWebDomain}/?c=${brandId}&m=${variantId}`}
                                                size={100}
                                                level="H"
                                                includeMargin
                                            />
                                            <Typography.Text style={{ marginTop: 8, fontSize: 'clamp(0.75rem, 0.7rem + 0.25vw, 0.85rem)' }}>
                                                Scan for VTO
                                            </Typography.Text>
                                        </div>
                                    </Descriptions.Item>
                                </Descriptions>

                                {/* Validation Status & Controls */}
                                {(role === 'Admin' || role === 'Modellista' || role === 'ModellerSupervisor') && (
                                    <Descriptions
                                        bordered
                                        size="small"
                                        column={1}
                                        title="Validation Status & Controls"
                                        style={{ marginTop: '16px' }}
                                    >

                                        <Descriptions.Item label="Last Update">
                                            {(() => {
                                                try {
                                                    let date;
                                                    let isFirebaseTimestamp = false;

                                                    if (variant.lastUpdate?.seconds) { // Firebase Timestamp
                                                        date = new Date(variant.lastUpdate.seconds * 1000);
                                                        isFirebaseTimestamp = true;
                                                    } else if (variant.lastUpdate) { // String date
                                                        date = new Date(variant.lastUpdate);
                                                    }

                                                    if (date && !isNaN(date.getTime())) {
                                                        const day = date.getDate().toString().padStart(2, '0');
                                                        const month = (date.getMonth() + 1).toString().padStart(2, '0');
                                                        const year = date.getFullYear();
                                                        const hours = date.getHours().toString().padStart(2, '0');
                                                        const minutes = date.getMinutes().toString().padStart(2, '0');

                                                        // Estrae il fuso orario dal timestamp se disponibile
                                                        let timeZone = '';
                                                        if (isFirebaseTimestamp) {
                                                            // Per i timestamp Firebase, usa toLocaleString per ottenere il fuso orario
                                                            const fullString = date.toLocaleString('it-IT', {
                                                                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                                                                timeZoneName: 'short'
                                                            });
                                                            const timeZoneMatch = fullString.match(/([A-Z]{2,4}[+-]?\d*)$/);
                                                            timeZone = timeZoneMatch ? timeZoneMatch[1] : '';
                                                        } else if (typeof variant.lastUpdate === 'string') {
                                                            // Per le stringhe, cerca il fuso orario alla fine
                                                            const timeZoneMatch = variant.lastUpdate.match(/([A-Z]{2,4}[+-]?\d+)$/i);
                                                            timeZone = timeZoneMatch ? timeZoneMatch[1] : '';
                                                        }

                                                        return `${day}/${month}/${year} Time:${hours}:${minutes}${timeZone ? ' ' + timeZone : ''}`;
                                                    }
                                                } catch (error) {
                                                    console.error('Error formatting date:', error);
                                                }

                                                return variant.lastUpdate || 'N/A';
                                            })()}
                                        </Descriptions.Item>

                                        <Descriptions.Item label="Assigned To">{variant?.assignedTo ? getUserFullName(variant.assignedTo) : 'Not assigned'}</Descriptions.Item>
                                        <Descriptions.Item label="Assignment Date">{(() => {
                                            if (!variant.assignmentDate) return 'Not assigned';

                                            // Gestisci il formato della data che potrebbe essere in diversi formati
                                            try {
                                                // Se è un timestamp Firebase
                                                if (variant.assignmentDate?.seconds) {
                                                    const date = new Date(variant.assignmentDate.seconds * 1000);
                                                    return date.toLocaleString('it-IT');
                                                }

                                                // Se è una stringa in formato italiano (dd/mm/yyyy)
                                                if (typeof variant.assignmentDate === 'string' && variant.assignmentDate.includes('/')) {
                                                    // La data è già in formato leggibile, restituiscila così com'è
                                                    return variant.assignmentDate;
                                                }

                                                // Altrimenti prova a creare un oggetto Date
                                                const date = new Date(variant.assignmentDate);
                                                if (!isNaN(date.getTime())) {
                                                    return date.toLocaleString('it-IT');
                                                }

                                                // Se nessun formato è valido, restituisci il valore originale
                                                return variant.assignmentDate;
                                            } catch (error) {
                                                console.error("Errore nella formattazione della data:", error);
                                                return variant.assignmentDate || 'Not assigned';
                                            }
                                        })()}</Descriptions.Item>
                                    </Descriptions>
                                )}

                                {/* Size Details */}
                                <Descriptions
                                    bordered
                                    size="small"
                                    column={1}
                                    title="Size Details"
                                    style={{ marginTop: '16px' }}
                                >
                                    <Descriptions.Item label="Bridge Size">{variant.size?.bridge || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="Lens Width">{variant.size?.lensWidth || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="Lens Height">{variant.size?.lensHeight || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="Temple Length">{variant.size?.templeLength || 'N/A'}</Descriptions.Item>
                                </Descriptions>

                                {/* Color Hex Values */}
                                {(role === 'Admin' || role === 'Modellista' || role === 'ModellerSupervisor') && (
                                    <div>
                                        <Typography.Title level={5} style={{ marginTop: '16px' }}>Color Hex Values</Typography.Title>
                                        <Table
                                            dataSource={hexColors}
                                            columns={[
                                                {
                                                    title: (
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                                                            <span>Attribute</span>
                                                            <CustomTooltip title="Attribute of the color hex value">
                                                                <InfoCircleOutlined style={{ marginLeft: 'clamp(0.4rem, 0.35rem + 0.25vw, 0.5rem)', cursor: 'pointer' }} />
                                                            </CustomTooltip>
                                                        </div>
                                                    ),
                                                    dataIndex: 'key',
                                                    key: 'key',
                                                    align: 'left',
                                                    width: '40%',
                                                },
                                                {
                                                    title: (
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                                            <span>Hex Value</span>
                                                            <CustomTooltip title="Hexadecimal color value">
                                                                <InfoCircleOutlined style={{ marginLeft: 'clamp(0.4rem, 0.35rem + 0.25vw, 0.5rem)', cursor: 'pointer' }} />
                                                            </CustomTooltip>
                                                        </div>
                                                    ),
                                                    dataIndex: 'value',
                                                    key: 'value',
                                                    align: 'right',
                                                    render: (text, record) => {
                                                        const pendingHex = pendingHexChanges[record.key];
                                                        const displayHex = pendingHex || text;

                                                        return (
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                                                                <div className="hex-value-container">
                                                                    <div className="custom-color-picker" style={{ width: '100%' }}>
                                                                        <ColorPicker
                                                                            value={displayHex}
                                                                            onChangeComplete={(color) => {
                                                                                const hex = color.toHexString();
                                                                                if (!originalHexValues[record.key]) {
                                                                                    setOriginalHexValues(prev => ({
                                                                                        ...prev,
                                                                                        [record.key]: text
                                                                                    }));
                                                                                }
                                                                                setPendingHexChanges(prev => ({
                                                                                    ...prev,
                                                                                    [record.key]: hex
                                                                                }));
                                                                            }}
                                                                            showText
                                                                            disabled={!isFieldEditable('hexColors')}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <Button
                                                                    icon={<svg xmlns="http://www.w3.org/2000/svg" height="16" viewBox="0 0 24 24" width="16" fill="currentColor"><path d="M0 0h24v24H0z" fill="none" /><path d="M19.11 2.29c-.39-.39-1.02-.39-1.41 0L15.47 4.52l-.35-.35c-.2-.2-.51-.2-.71 0l-.71.71c-.2.2-.2.51 0 .71l.35.35L3 16.59V21h4.41l10.17-10.17.35.35c.2.2.51.2.71 0l.71-.71c.2-.2.2-.51 0-.71l-.35-.35 2.23-2.23c.39-.39.39-1.02 0-1.41L19.11 2.29zM6.41 19H5v-1.41l9.06-9.06 1.41 1.41L6.41 19z" /></svg>}
                                                                    onClick={async () => {
                                                                        if ('EyeDropper' in window) {
                                                                            const eyeDropper = new window.EyeDropper();
                                                                            try {
                                                                                const result = await eyeDropper.open();
                                                                                const hex = result.sRGBHex;
                                                                                if (!originalHexValues[record.key]) {
                                                                                    setOriginalHexValues(prev => ({
                                                                                        ...prev,
                                                                                        [record.key]: text
                                                                                    }));
                                                                                }
                                                                                setPendingHexChanges(prev => ({
                                                                                    ...prev,
                                                                                    [record.key]: hex
                                                                                }));
                                                                            } catch (e) {
                                                                                console.warn("Eyedropper cancelled", e);
                                                                            }
                                                                        } else {
                                                                            showWarningNotification("Warning", "Eyedropper API not supported in your browser");
                                                                        }
                                                                    }}
                                                                    disabled={!isFieldEditable('hexColors')}
                                                                />
                                                                {pendingHex && (
                                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                                        <IconButton
                                                                            icon={<CheckOutlined />}
                                                                            tooltip="Confirm"
                                                                            style={buttonStyles.success}
                                                                            onClick={() => confirmPendingHexChange(record.key)}
                                                                        />
                                                                        <IconButton
                                                                            icon={<DeleteOutlined />}
                                                                            tooltip="Cancel"
                                                                            style={buttonStyles.error}
                                                                            onClick={() => cancelPendingHexChange(record.key)}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    }
                                                }
                                            ]}
                                            pagination={false}
                                            rowKey="key"
                                            bordered
                                            size="small"
                                            style={{ backgroundColor: "#fff", borderRadius: "12px", marginBottom: '24px' }}
                                        />
                                    </div>
                                )}

                                {/* Reference Images Section */}
                                {(role === 'Admin' || role === 'Modellista' || role === 'ModellerSupervisor') && (
                                    <div style={{ marginTop: '16px' }}>
                                        <Typography.Title level={5}>Reference Images</Typography.Title>
                                        <Table
                                            dataSource={[
                                                { key: 'poster', url: variant.poster },
                                                { key: 'poster2', url: variant.poster2 },
                                                { key: 'poster3', url: variant.poster3 },
                                                { key: 'poster4', url: variant.poster4 },
                                                { key: 'posterNoBackground', url: variant.posterNoBackground }
                                            ]}
                                            columns={columnsWithIcons}
                                            pagination={false}
                                            rowKey="key"
                                            size="small"
                                            style={{ backgroundColor: "#fff", borderRadius: "12px", marginBottom: '24px', width: '100%' }}
                                        />
                                    </div>
                                )}

                                {/* 3D Assets Section */}
                                {(role === 'Admin' || role === 'Modellista' || role === 'ModellerSupervisor') && (
                                    <div style={{ marginTop: '16px' }}>
                                        <Typography.Title level={5}>3D Assets</Typography.Title>
                                        <div style={{ position: 'relative' }}>
                                            <div style={{
                                                filter: !canView3DAssets(role) ? "blur(5px)" : "none"
                                            }}>
                                                <Table
                                                    dataSource={[
                                                        { key: 'urlGlobal', url: variant.urlGlobal, displayName: 'Url Glb Cut' },
                                                        { key: 'urlGlobalComplete', url: variant.urlGlobalComplete, displayName: 'Url Glb Complete' },
                                                        { key: 'urlUsdz', url: variant.urlUsdz, displayName: 'Url Usdz Cut' },
                                                        { key: 'urlUsdzComplete', url: variant.urlUsdzComplete, displayName: 'Url Usdz Complete' }
                                                    ].filter(item => item.key)}
                                                    columns={columnsWithIcons}
                                                    pagination={false}
                                                    rowKey="key"
                                                    size="small"

                                                    style={{ backgroundColor: "#fff", borderRadius: "12px", width: '100%' }}
                                                />
                                            </div>
                                            {!canView3DAssets(role) && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                                    zIndex: 1,
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    color: '#000',
                                                    fontSize: '1.2rem',
                                                    fontWeight: 'bold'
                                                }}>
                                                    You do not have permission to view this data
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </SimpleCard>
                    </Col>

                    {/* SimpleCard posizionata a destra  */}
                    <Col xs={24} sm={24} md={isRightCardExpanded ? 24 : 16}
                        style={{
                            transition: 'all 0.3s ease',
                            order: isRightCardExpanded ? 0 : 1,
                            position: isRightCardExpanded ? 'relative' : 'sticky',
                            top: isRightCardExpanded ? 'auto' : '20px',
                            height: isRightCardExpanded ? 'auto' : 'calc(100vh - 40px)',
                            zIndex: 10
                        }}>
                        <SimpleCard>
                            <div>
                                {/* Images Gallery Section */}
                                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                    <div style={{
                                        height: '43.75rem',
                                        position: 'relative',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center'
                                    }}>
                                        {/* Main Image Display */}
                                        <div style={{
                                            width: '100%',
                                            position: 'relative',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            height: '37.5rem',
                                            overflow: 'hidden'
                                        }}>
                                            {/* Buttons in top left corner */}
                                            {/* Action Buttons (Start, Validate, Decline) on the left */}
                                            <div style={{
                                                position: 'absolute',
                                                top: '15px',
                                                left: '15px',
                                                zIndex: 20,
                                                display: 'flex',
                                                gap: '10px'
                                            }}>
                                                {status.text === 'Incomplete' && variant.assignedTo === userEmail && (
                                                    <Button
                                                        type="primary"
                                                        size="middle"
                                                        style={{
                                                            backgroundColor: "#007bff",
                                                            borderColor: "#007bff"
                                                        }}
                                                        onClick={() => updateVariantStatus('start')}
                                                    >
                                                        Start
                                                    </Button>
                                                )}
                                                <Button
                                                    type="primary"
                                                    size="middle"
                                                    style={{
                                                        backgroundColor: "#28a745",
                                                        borderColor: "#28a745"
                                                    }}
                                                    onClick={() => updateVariantStatus("validate")}
                                                    disabled={isValidateDisabled(role, variant.status)}
                                                >
                                                    {variant.status === "In Pubblicazione" ? "Publish" : "Validate"}
                                                </Button>
                                                <Button
                                                    danger
                                                    size="middle"
                                                    onClick={() => updateVariantStatus("decline")}
                                                    disabled={isDeclineDisabled(role, variant.status)}
                                                >
                                                    Decline
                                                </Button>
                                            </div>

                                            {/* Try on VTO, Customize Preset, and Expand View Buttons on the right */}
                                            <div style={{
                                                position: 'absolute',
                                                top: '15px',
                                                right: '15px',
                                                zIndex: 20,
                                                display: 'flex',
                                                gap: '10px',
                                                alignItems: 'flex-start'
                                            }}>
                                                {/* Try on VTO Button - mostrato solo se esiste urlGlobal */}
                                                {variant.urlGlobal && (
                                                    <Button
                                                        type="link"
                                                        style={{
                                                            backgroundColor: '#f0f0f0',
                                                            border: 'none',
                                                            boxShadow: 'none',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            color: '#000000',
                                                            padding: '4px 12px',
                                                            borderRadius: '4px',
                                                            height: '32px'
                                                        }}
                                                        onClick={() => setIsVtoModalVisible(true)}
                                                    >
                                                        <img
                                                            src={TryOnIcon}
                                                            alt="Try On"
                                                            style={{
                                                                width: '35px',
                                                                height: '35px',
                                                                objectFit: 'contain'
                                                            }}
                                                        />
                                                        Try On VTO
                                                    </Button>
                                                )}

                                                {/* Edit Button - solo per Admin, Modellista, ModellerSupervisor */}
                                                {(role === 'Admin' || role === 'Modellista' || role === 'ModellerSupervisor') && (
                                                    <Button
                                                        type="link"
                                                        onClick={handleEditClick}
                                                        style={{
                                                            backgroundColor: '#f0f0f0',
                                                            border: 'none',
                                                            boxShadow: 'none',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            color: '#000000',
                                                            padding: '4px 12px',
                                                            borderRadius: '4px',
                                                            height: '32px'
                                                        }}
                                                    >
                                                        <EditOutlined
                                                            style={{
                                                                fontSize: '18px',
                                                                height: '18px',
                                                                width: '18px',
                                                                color: '#000'
                                                            }}
                                                        />
                                                        Customize Preset
                                                    </Button>
                                                )}

                                                {/* Expand View Button - disponibile a tutti */}
                                                <Button
                                                    type="text"
                                                    icon={isRightCardExpanded ? <CompressOutlined /> : <ExpandOutlined />}
                                                    onClick={() => setIsRightCardExpanded(!isRightCardExpanded)}
                                                    style={{
                                                        marginLeft: '8px',
                                                        fontSize: '14px',
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #d9d9d9'
                                                    }}
                                                >
                                                    {isRightCardExpanded ? 'Compress View' : 'Expand View'}
                                                </Button>
                                            </div>

                                            {/* Navigation Arrows */}
                                            {(() => {
                                                // Only include posters that have a URL and have loaded successfully
                                                const images = [];

                                                // Only add posters that have valid URLs and have loaded successfully
                                                if (variant.poster && validPosters.poster === true) {
                                                    images.push({ key: 'poster', url: variant.poster, label: 'Poster 1' });
                                                }
                                                if (variant.poster2 && validPosters.poster2 === true) {
                                                    images.push({ key: 'poster2', url: variant.poster2, label: 'Poster 2' });
                                                }
                                                if (variant.poster3 && validPosters.poster3 === true) {
                                                    images.push({ key: 'poster3', url: variant.poster3, label: 'Poster 3' });
                                                }
                                                if (variant.poster4 && validPosters.poster4 === true) {
                                                    images.push({ key: 'poster4', url: variant.poster4, label: 'Poster 4' });
                                                }

                                                // Add 1 to account for the 3D model at index 0
                                                const maxIndex = images.length;

                                                return images.length > 0 ? (
                                                    <>
                                                        <NavigateBeforeIcon
                                                            onClick={() => currentImageIndex > 0 && goToPrevImage()}
                                                            style={{
                                                                position: 'absolute',
                                                                left: '20px',
                                                                top: '50%',
                                                                transform: 'translateY(-50%)',
                                                                zIndex: 10,
                                                                cursor: currentImageIndex > 0 ? 'pointer' : 'not-allowed',
                                                                opacity: currentImageIndex === 0 ? 0.5 : 1,
                                                                fontSize: '2rem',
                                                                color: currentImageIndex > 0 ? COLORS.navigationIcon : COLORS.navigationIconDisabled,
                                                                transition: 'all 0.2s ease'
                                                            }}
                                                        />
                                                        <NavigateNextIcon
                                                            onClick={() => currentImageIndex < maxIndex && goToNextImage(maxIndex)}
                                                            style={{
                                                                position: 'absolute',
                                                                right: '20px',
                                                                top: '50%',
                                                                transform: 'translateY(-50%)',
                                                                zIndex: 10,
                                                                cursor: currentImageIndex < maxIndex ? 'pointer' : 'not-allowed',
                                                                opacity: currentImageIndex === maxIndex ? 0.5 : 1,
                                                                fontSize: '2rem',
                                                                color: currentImageIndex < maxIndex ? COLORS.navigationIcon : COLORS.navigationIconDisabled,
                                                                transition: 'all 0.2s ease'
                                                            }}
                                                        />
                                                    </>
                                                ) : null;
                                            })()}

                                            {/* Display Current Image or 3D Model */}
                                            {(() => {
                                                // Create array of all images
                                                // Only include posters that have a URL and have loaded successfully
                                                const allImages = [
                                                    { key: 'model_3d', is3DModel: true }
                                                ];

                                                // Only add posters that have valid URLs and have loaded successfully
                                                if (variant.poster && validPosters.poster === true) {
                                                    allImages.push({ key: 'poster', url: variant.poster });
                                                }
                                                if (variant.poster2 && validPosters.poster2 === true) {
                                                    allImages.push({ key: 'poster2', url: variant.poster2 });
                                                }
                                                if (variant.poster3 && validPosters.poster3 === true) {
                                                    allImages.push({ key: 'poster3', url: variant.poster3 });
                                                }
                                                if (variant.poster4 && validPosters.poster4 === true) {
                                                    allImages.push({ key: 'poster4', url: variant.poster4 });
                                                }

                                                if (allImages.length === 0) return null;

                                                return (
                                                    <>
                                                        {/* 3D Model - sempre presente ma visibile solo quando selezionato */}
                                                        <div style={{
                                                            width: '100%',
                                                            height: '37.5rem',
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            position: 'relative',
                                                            backgroundColor: '#f5f5f5',
                                                            borderRadius: '8px',
                                                            visibility: currentImageIndex === 0 ? 'visible' : 'hidden',
                                                            position: currentImageIndex === 0 ? 'relative' : 'absolute',
                                                            zIndex: currentImageIndex === 0 ? 2 : 1,
                                                        }}>
                                                            {
                                                                modelUrl ? (
                                                                    <>
                                                                        {iframeLoading && (
                                                                            <div style={{
                                                                                position: 'absolute',
                                                                                width: '100%',
                                                                                height: '100%',
                                                                                display: 'flex',
                                                                                justifyContent: 'center',
                                                                                alignItems: 'center',
                                                                                zIndex: 3,
                                                                                backgroundColor: COLORS.backgroundSemiTransparent
                                                                            }}>
                                                                                <Spin size="large" />
                                                                            </div>
                                                                        )}
                                                                        <iframe
                                                                            src={modelUrl}
                                                                            title="3D Model"
                                                                            style={{
                                                                                width: '100%',
                                                                                height: '100%',
                                                                                border: 'none',
                                                                                borderRadius: '4px',
                                                                                overflow: 'hidden'
                                                                            }}
                                                                            allowFullScreen
                                                                            onLoad={() => setIframeLoading(false)}
                                                                            onError={() => setIframeLoading(true)}
                                                                        ></iframe>
                                                                    </>
                                                                ) : (
                                                                    <div style={{
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        display: 'flex',
                                                                        justifyContent: 'center',
                                                                        alignItems: 'center',
                                                                        flexDirection: 'column'
                                                                    }}>
                                                                        <ThreeDRotationIcon style={{ fontSize: '64px', color: COLORS.modelIcon, marginBottom: '16px' }} />
                                                                        <div style={{ textAlign: 'center', fontWeight: 'bold' }}>3D Model not available</div>
                                                                    </div>
                                                                )
                                                            }
                                                        </div>

                                                        {/* Immagini - mostrate solo quando selezionate */}
                                                        {allImages.map((image, index) => {
                                                            // Salta il modello 3D che è già gestito sopra
                                                            if (image.is3DModel) return null;

                                                            return (
                                                                <div
                                                                    key={image.key}
                                                                    style={{
                                                                        display: currentImageIndex === index ? 'flex' : 'none',
                                                                        justifyContent: 'center',
                                                                        alignItems: 'center',
                                                                        width: '100%',
                                                                        height: '37.5rem',
                                                                        position: 'relative',
                                                                        zIndex: currentImageIndex === index ? 2 : 1,
                                                                    }}
                                                                >
                                                                    <img
                                                                        src={image.url}
                                                                        alt={`${variant.glassesName || 'Glasses'} - View ${index}`}
                                                                        style={{
                                                                            maxWidth: '100%',
                                                                            maxHeight: '37.5rem',
                                                                            objectFit: 'contain'
                                                                        }}
                                                                    />
                                                                </div>
                                                            );
                                                        })}
                                                    </>
                                                );
                                            })()}
                                        </div>

                                        {/* Thumbnails */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            marginTop: '15px',
                                            width: '100%',
                                            maxWidth: '600px',
                                            height: '60px'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                gap: '10px',
                                                flexWrap: 'wrap',
                                                width: '100%',
                                                height: '100%'
                                            }}>
                                                {/* Create an array of all images including 3D model */}
                                                {(() => {
                                                    // Create array of all images
                                                    // Only include posters that have a URL and have loaded successfully
                                                    const allImages = [
                                                        { key: 'model_3d', is3DModel: true, label: '3D Model' }
                                                    ];

                                                    // Only add posters that have valid URLs and have loaded successfully
                                                    if (variant.poster && validPosters.poster === true) {
                                                        allImages.push({ key: 'poster', url: variant.poster, label: 'Poster 1' });
                                                    }
                                                    if (variant.poster2 && validPosters.poster2 === true) {
                                                        allImages.push({ key: 'poster2', url: variant.poster2, label: 'Poster 2' });
                                                    }
                                                    if (variant.poster3 && validPosters.poster3 === true) {
                                                        allImages.push({ key: 'poster3', url: variant.poster3, label: 'Poster 3' });
                                                    }
                                                    if (variant.poster4 && validPosters.poster4 === true) {
                                                        allImages.push({ key: 'poster4', url: variant.poster4, label: 'Poster 4' });
                                                    }

                                                    return allImages.map((image, index) => (
                                                        <div
                                                            key={image.key}
                                                            onClick={() => setCurrentImageIndex(index)}
                                                            style={{
                                                                width: '60px',
                                                                height: '60px',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.3s ease',
                                                                borderRadius: '4px',
                                                                overflow: 'hidden',
                                                                border: index === currentImageIndex ? `2px solid ${COLORS.navigationIcon}` : '2px solid transparent',
                                                                opacity: index === currentImageIndex ? 1 : 0.7,
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                padding: '2px',
                                                                backgroundColor: image.is3DModel ? COLORS.modelIconBackground : COLORS.backgroundTransparent,
                                                                position: 'relative'
                                                            }}
                                                        >
                                                            {image.is3DModel ? (
                                                                <ThreeDRotationIcon style={{ fontSize: '32px', color: COLORS.modelIconDark }} />
                                                            ) : (
                                                                <img
                                                                    src={image.url}
                                                                    alt={image.label || `Thumbnail ${index + 1}`}
                                                                    style={{
                                                                        maxWidth: '100%',
                                                                        maxHeight: '100%',
                                                                        objectFit: 'contain'
                                                                    }}
                                                                />
                                                            )}
                                                        </div>
                                                    ));
                                                })()}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Glasses Name */}
                                    <Typography.Text style={{ fontWeight: 'bold', textAlign: 'center', display: 'block', marginTop: '15px', fontSize: '16px' }}>
                                        {variant.glassesName || 'N/A'}
                                    </Typography.Text>
                                </div>

                                {/* Images Gallery Section */}
                                {/* Only images gallery and related content should remain here. */}
                            </div>
                        </SimpleCard>
                    </Col>
                </Row>

                {/* Richiamo di tutte le modali utilizzate nel componente */}
                <VariantAssignedToModal
                    isVisible={isSingleAssignModalVisible}
                    onCancel={() => setIsSingleAssignModalVisible(false)}
                    variantId={variantId}
                    onSuccess={async () => {
                        setIsSingleAssignModalVisible(false);
                        try {
                            const variantRef = doc(db, "Variants", variantId);
                            const variantSnap = await getDoc(variantRef);
                            if (variantSnap.exists()) {
                                const updatedVariant = variantSnap.data();
                                setVariant(updatedVariant);
                            } else {
                            }
                        } catch (error) {
                            console.error("Errore durante il refresh della variante:", error);
                        }
                    }}
                    currentAssignee={variant.assignedTo}
                    assignmentDate={variant.assignmentDate}
                />

                <DeclineModal
                    isVisible={isDeclineModalVisible}
                    onSubmit={handleDeclineSubmit}
                    onCancel={() => setIsDeclineModalVisible(false)}
                    declineNote={declineNote}
                    setDeclineNote={setDeclineNote}
                    handleImageUpload={handleImageUpload}
                    handleRemoveImage={handleRemoveImage}
                    nameProduct={declineNote.nameProduct}
                    noteId={validationNoteId}
                />

                <TypeOfModellingModal
                    isVisible={isModellingModalVisible}
                    onSubmit={handleModellingSubmit}
                    onCancel={() => {
                        setIsModellingModalVisible(false);
                        setPendingValidationAction(null);
                    }}
                    isLoading={modellingLoading}
                />

                <FullScreenModal
                    visible={isEditModalVisible}
                    onClose={handleEditModalClose}
                    iframe={true}
                >
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <iframe
                            src={`${window.location.origin}/materialpresets/${variantId}?debug=true`}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                overflow: 'hidden',
                                display: 'block'
                            }}
                            scrolling="no"
                            frameBorder="0"
                            allowFullScreen
                        />
                    </div>
                </FullScreenModal>

                <FullScreenModal
                    visible={isVtoModalVisible}
                    onClose={() => setIsVtoModalVisible(false)}
                    title="Virtual Try On"
                    iframe={true}
                    closeButtonPosition='top'
                >
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <iframe
                            // src={`https://www.${mainBrandWebDomain}/?c=${brandId}&m=${variantId}`}
                            src={`https://www.${process.env.REACT_APP_TRY_ON_VTO_FRAME_VALIDATION}/?c=${brandId}&m=${variantId}&debug=true`}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                overflow: 'hidden',
                                display: 'block'
                            }}
                            scrolling="no"
                            frameBorder="0"
                            allowFullScreen
                            allow="camera"
                            title="Virtual Try On"
                        />
                    </div>
                </FullScreenModal>
                {/* Modale di conferma per azioni */}
                <SmallModal
                    visible={isConfirmModalVisible}
                    onClose={() => {
                        setIsConfirmModalVisible(false);
                        setPendingAction(null);
                    }}
                    onConfirm={executeConfirmedAction}
                    title=""
                    confirmText="Yes"
                    cancelText="Cancel"
                >
                    <div style={{ textAlign: 'center', padding: '16px 0' }}>
                        {(() => {
                            if (!pendingAction || !variant) {
                                return (
                                    <Typography.Text style={{ fontSize: '16px', color: '#666' }}>
                                        Are you sure you want to proceed with this action?
                                    </Typography.Text>
                                );
                            }

                            const nextStatus = determineNextStatus(role, variant.status, pendingAction);
                            const statusMapping = {
                                "Rev. Modellista": "Modelist Rev.",
                                "Rev. Cliente": "Client Rev.",
                                "Rev. Spaarkly": "Spaarkly Rev.",
                                "Incompleto": "Incomplete",
                                "In Pubblicazione": "In Publication",
                                "Pubblicato": "Published"
                            };

                            const currentStatusDisplay = statusMapping[variant.status] || variant.status;
                            const nextStatusDisplay = statusMapping[nextStatus] || nextStatus;

                            // Componente visuale per mostrare la transizione di stato: [Stato Attuale] → [Stato Destinazione]
                            // Utilizza i ButtonState esistenti per mantenere coerenza visiva con il design system
                            // Componente per mostrare il cambio di stato con i ButtonState
                            const StatusChangeDisplay = () => (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '16px',
                                    marginTop: '20px'
                                }}>
                                    <ButtonState
                                        state={variant.status}
                                        text={currentStatusDisplay}
                                    />
                                    <div style={{
                                        fontSize: '20px',
                                        fontWeight: 'bold',
                                        color: '#000000'
                                    }}>
                                        →
                                    </div>
                                    <ButtonState
                                        state={nextStatus}
                                        text={nextStatusDisplay}
                                    />
                                </div>
                            );

                            if (pendingAction === "validate" && variant.status === "In Pubblicazione") {
                                return (
                                    <div>
                                        <Typography.Title level={4} style={{ color: '#000', marginBottom: '12px' }}>
                                            Ready to publish this variant?
                                        </Typography.Title>
                                        <Typography.Text style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '8px' }}>
                                            Click Yes if you want to proceed.
                                        </Typography.Text>
                                        <Typography.Text style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '16px' }}>
                                            Once confirmed, it will be added to the ARShades Library and will be immediately available for import into your catalogues.
                                        </Typography.Text>
                                        <StatusChangeDisplay />
                                    </div>
                                );
                            } else if (pendingAction === "validate") {
                                return (
                                    <div>
                                        <Typography.Text style={{ fontSize: '16px', color: '#666', display: 'block', marginBottom: '16px' }}>
                                            Are you sure you want to validate this variant?
                                        </Typography.Text>
                                        <StatusChangeDisplay />
                                    </div>
                                );
                            } else if (pendingAction === "decline") {
                                return (
                                    <div>
                                        <Typography.Text style={{ fontSize: '16px', color: '#666', display: 'block', marginBottom: '16px' }}>
                                            Are you sure you want to decline this variant?
                                        </Typography.Text>
                                        <StatusChangeDisplay />
                                    </div>
                                );
                            } else if (pendingAction === "start") {
                                return (
                                    <div>
                                        <Typography.Text style={{ fontSize: '16px', color: '#666', display: 'block', marginBottom: '16px' }}>
                                            Are you sure you want to start working on this variant?
                                        </Typography.Text>
                                        <StatusChangeDisplay />
                                    </div>
                                );
                            }

                            return (
                                <Typography.Text style={{ fontSize: '16px', color: '#666' }}>
                                    Are you sure you want to proceed with this action?
                                </Typography.Text>
                            );
                        })()}
                    </div>
                </SmallModal>

                {/* Fine richiamo modali */}

            </div>
        </App >
    );
}

export default VariantDetails;